/**
 * Flags de producto — qué superficie se sirve en cada entorno.
 *
 * Los dos primeros son de INFRAESTRUCTURA (qué API consume la web). Los demás son de PRODUCTO:
 * funcionalidad construida y probada que todavía no se le muestra —o no se le habilita— al usuario.
 *
 * Todos son `VITE_*`, así que se resuelven en el BUILD y viajan en el bundle: cambiarlos exige
 * rebuild, no alcanza con reiniciar. Nada secreto puede vivir acá.
 */
export const FEATURES = {
  /** Consume `/api/v2/admin/*` para auth, catálogos, clientes, usuarios y roles. */
  apiTokensV2: import.meta.env.VITE_FEATURE_API_TOKENS_V2 === 'true',
  webAuthV2: import.meta.env.VITE_FEATURE_WEB_AUTH_V2 === 'true',

  /**
   * Portal del cliente, de punta a punta. Cubre cuatro superficies que parecen distintas y son
   * una sola función:
   *
   * 1. El formulario público y las ocho pantallas de revisión.
   * 2. El dashboard de revalidación anual.
   * 3. **El motor de flujos** (`/flows`), que es su pantalla de configuración: define qué
   *    secciones llena el cliente. Sin portal, configurar flujos de portal no sirve para nada.
   * 4. Los rastros en pantallas que SÍ salen: las columnas de vencimiento/vigencia SAP en las
   *    listas de clientes, sus KPIs, el chip de filtro por validación y el alta rápida —que no
   *    *tiene* autogestión, *es* autogestión: crea el cliente y le manda el formulario.
   *
   * **Un solo flag y no cuatro, a propósito.** Dos flags que siempre tienen que moverse juntos no
   * son granularidad: son un modo de falla. Prender uno y olvidar el otro deja media función a la
   * vista, que es justo lo que el flag existe para evitar.
   *
   * No confundir con `/workflows` («Flujos de trabajo»), que es de negociaciones y NO va acá.
   *
   * Va junto con `CRONS_ENABLED`: los crons producen las solicitudes y los correos, esto muestra
   * la superficie que las administra.
   */
  clientPortal: import.meta.env.VITE_FEATURE_CLIENT_PORTAL === 'true',

  /**
   * ¿Ya está publicada en las tiendas la app móvil del cliente?
   *
   * Es el ÚNICO flag de la app móvil que queda en la web, y no esconde nada: las pantallas de
   * cuentas, accesos, avisos y cartapacio están siempre disponibles. Lo que apaga es lo que
   * ALCANZA a un cliente real, que con la app sin publicar le anunciaría algo que no puede
   * descargar —y un correo o un push no se deshacen—:
   *
   * 1. Enviar o reenviar la invitación.
   * 2. Abrir una cuenta o un grant.
   * 3. «Liberar acceso» (incluido el switch de la autorización de una venta).
   * 4. Enviar un aviso.
   *
   * Apagado, esos controles se ven DESHABILITADOS con «Disponible cuando la app esté publicada»
   * (`shared/config/client-app-launch.ts`). Espeja a `FEATURE_CLIENT_APP_LAUNCHED` de la API, que
   * es quien de verdad frena: si los dos flags discrepan, el servidor contesta
   * `409 CLIENT_APP_NOT_LAUNCHED` y la pantalla lo traduce por código.
   *
   * ⚠️ Es un flag de LANZAMIENTO y muere con él: el día que la app salga a las tiendas se prende
   * en el servicio web y se borra de acá, de `build-feature-flags.js`, del `Dockerfile` y de
   * `turbo.json` (ADR 0006).
   *
   * Publicar un documento NO depende de este flag: el cartapacio es del back-office y la subida
   * y la publicación están siempre habilitadas. Lo que frena el servidor es el push de «documento
   * publicado», no la publicación.
   */
  clientAppLaunched: import.meta.env.VITE_FEATURE_CLIENT_APP_LAUNCHED === 'true'
} as const
